import type { DocumentResponse } from '@contracts/document/DocumentResponse';
import type { AddNoteRequest } from '@contracts/note/AddNoteRequest';
import type { IssuePermitApplicationResponse } from '@contracts/order/IssuePermitApplicationResponse';
import type { PermitSummary as PermitSummaryResponse } from '@contracts/order/OrderListResponse';
import type { OrderPermitApplicationResponse } from '@contracts/order/OrderResponse';
import type { TransactionCurrency } from '@contracts/payment/Currency';
import type {
  PermitFiltersOptions,
  PermitSortBy,
} from '@contracts/permitSearch/PermitFiltersOptions';
import type { PermitListResponse } from '@contracts/permitSearch/PermitListResponse';
import { roundToTwoDecimalsString } from '@contracts/utils/RoundingAmount';
import { AuthenticatedClient } from '@src/common/services/AuthenticatedClient';
import { Timestamp } from '@src/common/time/Timestamp';
import type { OrderPermitApplication } from '@src/orders/domain/Order';
import type { PermitStatus } from '@src/orders/domain/PermitApplication';
import { PermitApplication } from '@src/orders/domain/PermitApplication';
import type { PermitSummary } from '@src/orders/domain/PermitSummary';
import type { DuplicationTrigger } from '@src/orders/hooks/useDuplicatePermitAsNew';
import { parsePermitSummary } from '@src/orders/services/OrdersClient';
import type {
  CompletePermitFields,
  DeliverPermitFields,
  ErrorPermitFields,
} from '@src/permits/completePermit/fields/CompletePermitFields';
import type { DraftPermitStatementFields } from '@src/permits/completePermit/fields/DraftPermitStatementFields';
import type { SelfIssuedGeneratePdfFields } from '@src/permits/completePermit/fields/SelfIssuedGeneratePdfFields';

export type DateRange =
  | {
      from: string | undefined;
      to: string | undefined;
    }
  | undefined;
export type SearchPermitFilters =
  | SearchPermitForReportFilters
  | SearchPermitBasicFilters;
export interface SearchPermitForReportFilters {
  type?: string[];
  permitName?: string;
  submissionDate?: DateRange;
  client?: string[];
  billTo?: string[];
  jurisdiction?: string[];
  status?: string[];
  stateId?: string;
  tractorUnitNumber?: string;
  tractorLicensePlateNumber?: string;
  invoiceNumber?: string;
  tractorVIN?: string;
  sortBy?: PermitSortBy;
  claimedBy?: string;
}

export interface SearchPermitBasicFilters {
  type?: string[];
  permitName?: string;
  poNumber?: string;
  client?: string[];
  jurisdiction?: string[];
  status?: string[];
  stateId?: string;
  effectiveDate?: DateRange;
  submissionDate?: DateRange | undefined;
  tractorUnitNumber?: string;
  tractorLicensePlateNumber?: string;
  sortBy?: PermitSortBy;
  claimedBy?: string;
}

export const PermitApplicationClient = {
  searchPermits: async (
    filters: SearchPermitFilters = {},
  ): Promise<PermitListResponse> => {
    const { data } = await AuthenticatedClient.get<PermitListResponse>(
      '/permits',
      {
        params: {
          sortByPermit:
            filters.sortBy &&
            `${filters.sortBy.field.toString()}:${filters.sortBy.direction.toString()}`,
          ...filters,
        },
      },
    );
    return data;
  },
  searchPermitsWithFees: async (
    filters: SearchPermitForReportFilters = {},
  ): Promise<PermitListResponse> => {
    const { data } = await AuthenticatedClient.get<PermitListResponse>(
      '/permitsWithFees',
      {
        params: {
          sortByPermit:
            filters.sortBy &&
            `${filters.sortBy.field.toString()}:${filters.sortBy.direction.toString()}`,
          ...filters,
        },
      },
    );
    return data;
  },
  getPermitFiltersOptions: async (): Promise<PermitFiltersOptions> => {
    const { data } = await AuthenticatedClient.get<PermitFiltersOptions>(
      '/permits/filters/options',
    );
    return data;
  },
  findPermitApplication: async (
    permitApplicationId: string,
  ): Promise<OrderPermitApplication> => {
    const { data: response } =
      await AuthenticatedClient.get<OrderPermitApplicationResponse>(
        `/permits/${permitApplicationId}`,
      );
    return parsePermitApplication(response);
  },
  submit: async ({
    confirmationNumber,
    permitApplicationId,
  }: {
    confirmationNumber: string;
    permitApplicationId: string;
  }) => {
    await AuthenticatedClient.post<OrderPermitApplicationResponse>(
      `/permits/${permitApplicationId}:submit`,
      { confirmationNumber },
    );
  },
  saveSubmissionAsDraft: async ({
    confirmationNumber,
    permitApplicationId,
  }: {
    confirmationNumber: string;
    permitApplicationId: string;
  }) => {
    await AuthenticatedClient.post<OrderPermitApplicationResponse>(
      `/permits/${permitApplicationId}:saveSubmissionAsDraft`,
      { confirmationNumber },
    );
  },
  updateStatement: async ({
    permitApplicationId,
    fields: {
      jurisdictionFee,
      serviceFee,
      effectiveDate,
      expirationDate,
      statePermitNumber,
    },
  }: {
    permitApplicationId: string;
    fields: DraftPermitStatementFields;
  }) => {
    await AuthenticatedClient.post(
      `/permits/${permitApplicationId}:updateStatement`,
      {
        fees: {
          jurisdictionFee: jurisdictionFee
            ? roundToTwoDecimalsString(jurisdictionFee)
            : undefined,
          serviceFee: serviceFee
            ? roundToTwoDecimalsString(serviceFee)
            : undefined,
        },
        effectiveDate: effectiveDate.toISOString(),
        expirationDate: expirationDate?.toISOString(),
        statePermitNumber,
      },
    );
  },
  generateSelfIssuedPdf: async ({
    permitApplicationId,
    fields: { effectiveDate, expirationDate, statePermitNumber },
  }: {
    permitApplicationId: string;
    fields: SelfIssuedGeneratePdfFields;
  }) => {
    const { data } = await AuthenticatedClient.post<DocumentResponse>(
      `/permits/${permitApplicationId}/generateSelfIssuedPdf`,
      {
        effectiveDate: effectiveDate.toISOString(),
        expirationDate: expirationDate?.toISOString(),
        statePermitNumber,
      },
    );
    return {
      documentId: data.id,
      key: data.key,
      sendToClient: data.sendToClient,
      showToClient: data.showToClient,
      displayName: data.displayName,
    };
  },
  issueApplication: async ({
    permitApplicationId,
    completePermitFields,
  }: {
    permitApplicationId: string;
    completePermitFields: Omit<DeliverPermitFields, 'completionType'>;
  }): Promise<IssuePermitApplicationResponse> => {
    const { data } =
      await AuthenticatedClient.post<IssuePermitApplicationResponse>(
        `/permits/${permitApplicationId}:issue`,
        assembleFieldsToRequest({ permit: completePermitFields }),
      );
    return data;
  },
  issueError: async ({
    permitApplicationId,
    completePermitFields,
    note,
  }: {
    permitApplicationId: string;
    completePermitFields: Omit<ErrorPermitFields, 'completionType'>;
    note: AddNoteRequest;
  }) => {
    await AuthenticatedClient.post(
      `/permits/${permitApplicationId}:issueError`,
      assembleFieldsToRequest({ permit: completePermitFields, note }),
    );
  },
  markAsDenied: async ({
    permitApplicationId,
    completePermitFields,
    note,
  }: {
    permitApplicationId: string;
    completePermitFields: Omit<ErrorPermitFields, 'completionType'>;
    note: AddNoteRequest;
  }) => {
    await AuthenticatedClient.post(
      `/permits/${permitApplicationId}:markAsDenied`,
      assembleFieldsToRequest({ permit: completePermitFields, note }),
    );
  },
  duplicateAsNew: async ({
    permitApplicationId,
    trigger,
  }: {
    permitApplicationId: string;
    trigger: DuplicationTrigger;
    note?: AddNoteRequest;
  }) => {
    await AuthenticatedClient.post(
      `/permits/${permitApplicationId}:duplicate`,
      {
        trigger,
      },
    );
  },
  keepPermitAsIs: async ({
    permitApplicationId,
  }: {
    permitApplicationId: string;
  }) => {
    await AuthenticatedClient.post(`/permits/${permitApplicationId}:keepAsIs`);
  },
  replacePermitType: async ({
    permitApplicationId,
    newPermitTypeOriginalId,
    note,
  }: {
    permitApplicationId: string;
    newPermitTypeOriginalId: string;
    note?: AddNoteRequest;
  }) => {
    await AuthenticatedClient.post(
      `/permits/${permitApplicationId}:replacePermitType`,
      { newPermitTypeOriginalId, note },
    );
  },
  mergePermits: async ({
    permitApplicationIds,
  }: {
    permitApplicationIds: string[];
  }): Promise<Blob> => {
    const { data } = await AuthenticatedClient.post(
      '/permits/merge',
      { permitApplicationIds },
      { responseType: 'blob' },
    );
    return data;
  },
  reopenForCorrection: async ({
    permitApplicationId,
  }: {
    permitApplicationId: string;
  }): Promise<PermitSummary> => {
    const { data } = await AuthenticatedClient.post<PermitSummaryResponse>(
      `/permits/${permitApplicationId}:reopen`,
    );
    return parsePermitSummary(data);
  },
};

export const parsePermitApplication = (
  applicationResponse: OrderPermitApplicationResponse,
): OrderPermitApplication => ({
  application: new PermitApplication({
    ...applicationResponse,
    statePermitNumber: applicationResponse.statePermitNumber,
    expirationDate: applicationResponse.expirationDate
      ? Timestamp.from(applicationResponse.expirationDate)
      : undefined,
    effectiveDate: Timestamp.from(applicationResponse.effectiveDate),
    createdAt: Timestamp.from(applicationResponse.createdAt),
    updatedAt: Timestamp.from(applicationResponse.updatedAt),
    entriesUpdatedAt: Timestamp.from(applicationResponse.entriesUpdatedAt),
    status: applicationResponse.status as PermitStatus,
    statePermitDocument: applicationResponse.statePermitDocument
      ? {
          documentId: applicationResponse.statePermitDocument.id,
          key: applicationResponse.statePermitDocument.key,
          sendToClient: applicationResponse.statePermitDocument.sendToClient,
          showToClient: applicationResponse.statePermitDocument.showToClient,
          displayName: applicationResponse.statePermitDocument.displayName,
        }
      : undefined,
    documents:
      applicationResponse.documents?.map((document) => ({
        documentId: document.id,
        key: document.key,
        sendToClient: document.sendToClient,
        showToClient: document.showToClient,
      })) ?? [],
    otherDocuments:
      applicationResponse.otherDocuments?.map((document) => ({
        documentId: document.id,
        key: document.key,
        sendToClient: document.sendToClient,
        showToClient: document.showToClient,
      })) ?? [],
    conditionsSheets: applicationResponse.conditionsSheets,
  }),
  permitTypeName: applicationResponse.permitTypeName,
  permitType: applicationResponse.permitType,
  permitTypeCurrency:
    applicationResponse.permitTypeCurrency as TransactionCurrency,
  permitTypeJurisdiction: applicationResponse.permitTypeJurisdiction,
  permitTypeJurisdictionUrl: applicationResponse.permitTypeJurisdictionUrl,
  permitTypeIsSelfIssued: applicationResponse.permitTypeIsSelfIssued,
  permitTypeIsActive: applicationResponse.permitTypeIsActive,
  validateExpirationDate: applicationResponse.validateExpirationDate,
  validateExpirationTime: applicationResponse.validateExpirationTime,
  instructions: applicationResponse.instructions,
  submission: applicationResponse.submission
    ? {
        confirmationNumber: applicationResponse.submission.confirmationNumber,
        submissionDate: applicationResponse.submission?.submissionDate
          ? Timestamp.from(applicationResponse.submission.submissionDate)
          : null,
      }
    : undefined,
  fees: applicationResponse.fees
    ? {
        jurisdictionFee: +applicationResponse.fees.jurisdictionFee,
        serviceFee: +applicationResponse.fees.serviceFee,
      }
    : undefined,
});

const assembleFieldsToRequest = ({
  permit: {
    jurisdictionFee = 0,
    serviceFee = 0,
    effectiveDate,
    expirationDate,
    statePermitNumber,
    conditionsSheets,
  },
  note,
}: {
  permit: Omit<CompletePermitFields, 'completionType'>;
  note?: AddNoteRequest;
}) => ({
  fees: {
    jurisdictionFee: roundToTwoDecimalsString(jurisdictionFee),
    serviceFee: roundToTwoDecimalsString(serviceFee),
  },
  effectiveDate: effectiveDate?.toISOString(),
  expirationDate: expirationDate?.toISOString(),
  conditionsSheets,
  statePermitNumber,
  note,
});
